/**
 * AnimatedDemoPresentation Component
 *
 * Main orchestrator for the animated demo presentation.
 * Manages step progression, timing, and coordinates all child animations.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MarkdownWYSIWYGEditor } from '@/components/common/MarkdownWYSIWYGEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from 'src/context/ToastContext';
import { StepPill, StepProgressBar, type StepStatus } from './StepPill';
import {
  ThinkingAnimation,
  CurrencyRateDisplay,
  ValidationSummary,
} from './ThinkingAnimation';
import { AnimatedDataTable, DataTransitionWrapper } from './AnimatedDataTable';
import {
  generateOriginalData,
  generateAfterDatesData,
  generateAfterCleanTextData,
  generateAfterCurrencyData,
  generateAfterValidationData,
  generateFinalData,
  CAD_TO_USD_RATE,
} from './demoDataGenerators';
import paths from '@/paths';
import { AlertTriangle, Mail, ArrowRight } from 'lucide-react';

// const stepDurations = {
//   file: 12000,
//   dates: 16000,
//   text: 16000,
//   currency: 19000,
//   validation: 16000,
//   submit: 12000,
// };
// const stepDurations = {
//   file: 400,
//   dates: 600,
//   text: 600,
//   currency: 600,
//   validation: 600,
//   submit: 400,
// };

const stepDurations = {
  file: 4000,
  dates: 6000,
  text: 6000,
  currency: 6000,
  validation: 6000,
  submit: 4000,
};

// Define step-specific timing for highlights and transforms
// This controls when columns get highlighted (yellow) and when data transforms (green)
type StepAction = {
  highlightColumns: number[]; // Which columns to highlight
  highlightAtMessage: number; // Message index to start yellow highlight
  transformAtMessage: number; // Message index to apply transform (data changes + green)
  showCardAtMessage?: number; // Optional: when to show special card
};

const STEP_ACTIONS: Record<number, StepAction> = {
  // Step 2: Parse Dates - highlight date column (0), transform on final message
  2: { highlightColumns: [0], highlightAtMessage: 0, transformAtMessage: 2 },
  // Step 3: Clean Text - highlight description column (2), transform on final message
  3: { highlightColumns: [2], highlightAtMessage: 0, transformAtMessage: 2 },
  // Step 4: Currency - highlight price columns, show rate card, transform on final
  4: {
    highlightColumns: [4, 5, 6, 8],
    highlightAtMessage: 0,
    transformAtMessage: 2,
    showCardAtMessage: 1,
  },
  // Step 5: Validation - highlight on cross-ref message, transform + show summary on final
  5: {
    highlightColumns: [9],
    highlightAtMessage: 1,
    transformAtMessage: 2,
    showCardAtMessage: 2,
  },
  // Step 6: Final - just transform headers on final message
  6: { highlightColumns: [], highlightAtMessage: 0, transformAtMessage: 1 },
};

// Highlight states for columns
type HighlightState = 'none' | 'analyzing' | 'complete';

// Step configuration
const STEPS = [
  {
    id: 1,
    title: 'Processing File',
    description: 'Reading and parsing the uploaded spreadsheet file',
    thinkingMessages: [
      'Loading file contents...',
      'Detecting file format (CSV)',
      'Parsing 40 rows of data...',
    ],
    stepType: 'file' as const,
    duration: stepDurations.file,
  },
  {
    id: 2,
    title: 'Parse Dates',
    description: 'Standardizing date formats to ISO 8601 (YYYY-MM-DD)',
    thinkingMessages: [
      'Analyzing date column formats...',
      'Found 3 different date formats',
      'Standardizing to YYYY-MM-DD...',
    ],
    stepType: 'dates' as const,
    duration: stepDurations.dates,
  },
  {
    id: 3,
    title: 'Clean Text',
    description: 'Removing extra whitespace and expanding abbreviations',
    thinkingMessages: [
      'Scanning product descriptions...',
      'Removing extra whitespace',
      'Expanding abbreviations (Elec. -> Electrical)...',
    ],
    stepType: 'text' as const,
    duration: stepDurations.text,
  },
  {
    id: 4,
    title: 'Currency Conversion',
    description: 'Converting prices from CAD to USD',
    thinkingMessages: [
      'Fetching CAD/USD exchange rate...',
      'Rate: 1 CAD = 0.74 USD',
      'Converting price columns...',
    ],
    stepType: 'currency' as const,
    duration: stepDurations.currency,
  },
  {
    id: 5,
    title: 'Validate Against AP',
    description: 'Cross-referencing with AP invoice records',
    thinkingMessages: [
      'Connecting to AP system...',
      'Cross-referencing 40 transactions...',
      'Found 32 matches, 8 missing transactions',
    ],
    stepType: 'validation' as const,
    duration: stepDurations.validation,
  },
  {
    id: 6,
    title: 'Ready to Submit',
    description: 'All transformations complete - ready for submission',
    thinkingMessages: ['Finalizing data format...', 'Validation complete!'],
    stepType: 'submit' as const,
    duration: stepDurations.submit,
  },
];

export interface AnimatedDemoPresentationProps {
  fileName: string;
  onComplete?: () => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  autoStart?: boolean;
}

export function AnimatedDemoPresentation({
  fileName,
  onComplete,
  onSubmit,
  isSubmitting = false,
  autoStart = true,
}: AnimatedDemoPresentationProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStatuses, setStepStatuses] = useState<Record<number, StepStatus>>(
    {},
  );
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoStart);
  const [showCurrencyRate, setShowCurrencyRate] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [presentationComplete, setPresentationComplete] = useState(false);
  // Track which step's data we're displaying (updates at transformAtMessage)
  const [displayedDataStep, setDisplayedDataStep] = useState(0);
  // Track highlight state: 'none' | 'analyzing' | 'complete'
  const [highlightState, setHighlightState] = useState<HighlightState>('none');
  // Track which columns are currently highlighted
  const [activeHighlightColumns, setActiveHighlightColumns] = useState<
    number[]
  >([]);

  // AP validation pause state
  const [isApPaused, setIsApPaused] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState(
    'Missing AP Transactions - Reconciliation Required',
  );
  const [emailBody, setEmailBody] = useState(
    `Dear Lumina,

We have identified 8 transactions from your recent rebate submission that could not be matched against our Accounts Payable records.

Please review the attached list of missing transactions and provide the corresponding invoice documentation at your earliest convenience.

Transaction IDs requiring documentation:
- TXN-2026-1847
- TXN-2026-1852
- TXN-2026-1856
- TXN-2026-1863
- TXN-2026-1871
- TXN-2026-1879
- TXN-2026-1884
- TXN-2026-1891

Please respond within 5 business days to avoid processing delays.

Best regards,
Praxis Team`,
  );

  // Get current step config
  const currentStepConfig = STEPS[currentStep - 1];
  const currentStepAction = STEP_ACTIONS[currentStep];

  // Get data for the displayed step
  const currentData = useMemo(() => {
    switch (displayedDataStep) {
      case 0:
        return [];
      case 1:
        return generateOriginalData();
      case 2:
        return generateAfterDatesData();
      case 3:
        return generateAfterCleanTextData();
      case 4:
        return generateAfterCurrencyData();
      case 5:
        return generateAfterValidationData();
      case 6:
        return generateFinalData();
      default:
        return generateFinalData();
    }
  }, [displayedDataStep]);

  // Get highlighted columns - now based on active highlights during step processing
  const highlightedColumns = useMemo(() => {
    if (highlightState === 'none') return [];
    return activeHighlightColumns;
  }, [highlightState, activeHighlightColumns]);

  // Complete current step and move to next
  const completeStep = useCallback(() => {
    if (currentStep === 0) return;

    // Mark current step as complete
    setStepStatuses((prev) => ({
      ...prev,
      [currentStep]: 'complete',
    }));

    // Hide special displays
    setShowCurrencyRate(false);

    // Reset highlight state for next step
    setHighlightState('none');
    setActiveHighlightColumns([]);

    // Check if presentation is complete
    if (currentStep >= STEPS.length) {
      setPresentationComplete(true);
      setIsAutoPlaying(false);
      onComplete?.();
      return;
    }

    // Pause after step 5 (AP Validation) to show decision panel
    if (currentStep === 5) {
      setIsAutoPlaying(false);
      setIsApPaused(true);
      return;
    }

    // Hide validation summary only when not paused
    setShowValidationSummary(false);

    // Move to next step
    setCurrentStep((prev) => prev + 1);
    setCurrentMessageIndex(0);
    setStepStatuses((prev) => ({
      ...prev,
      [currentStep + 1]: 'processing',
    }));
  }, [currentStep, onComplete]);

  // Handle proceed action from AP pause
  const handleProceed = useCallback(() => {
    setIsApPaused(false);
    setShowValidationSummary(false);

    // Move to next step
    setCurrentStep((prev) => prev + 1);
    setCurrentMessageIndex(0);
    setStepStatuses((prev) => ({
      ...prev,
      [currentStep + 1]: 'processing',
    }));
    setIsAutoPlaying(true);
  }, [currentStep]);

  // Handle email Lumina action
  const handleEmailLumina = useCallback(() => {
    setShowEmailModal(true);
  }, []);

  // Handle sending email
  const handleSendEmail = useCallback(() => {
    setShowEmailModal(false);
    showToast({
      title: 'Email Sent',
      message: 'Your email has been sent to Lumina successfully.',
      color: 'success',
      icon: 'check',
    });
    // Redirect to email queue page
    navigate({ to: paths.authenticated.rebates.uploadDataEmailQueue });
  }, [navigate, showToast]);

  // Start a step
  const startStep = useCallback((stepNum: number) => {
    setCurrentStep(stepNum);
    setCurrentMessageIndex(0);
    setStepStatuses((prev) => ({
      ...prev,
      [stepNum]: 'processing',
    }));
    // For step 1, immediately show the original data
    if (stepNum === 1) {
      setDisplayedDataStep(1);
    }
  }, []);

  // Handle mid-step actions based on message index
  useEffect(() => {
    if (!currentStepAction || currentStep === 0) return;

    const {
      highlightColumns,
      highlightAtMessage,
      transformAtMessage,
      showCardAtMessage,
    } = currentStepAction;

    // Start analyzing highlight at the right message
    if (
      currentMessageIndex === highlightAtMessage &&
      highlightColumns.length > 0
    ) {
      setActiveHighlightColumns(highlightColumns);
      setHighlightState('analyzing');
    }

    // Show special cards at the right message
    if (
      showCardAtMessage !== undefined &&
      currentMessageIndex === showCardAtMessage
    ) {
      if (currentStep === 4) {
        setShowCurrencyRate(true);
      } else if (currentStep === 5) {
        setShowValidationSummary(true);
      }
    }

    // Transform data at the right message (this is the key change - happens mid-step)
    if (currentMessageIndex === transformAtMessage) {
      // Update data to show transformation result
      setDisplayedDataStep(currentStep);
      // Change highlight to complete (green)
      setHighlightState('complete');
      // Trigger transition animation
      setIsTransitioning(true);
      setTimeout(() => setIsTransitioning(false), 1500);
    }
  }, [currentStep, currentMessageIndex, currentStepAction]);

  // Auto-play logic
  useEffect(() => {
    if (!isAutoPlaying || presentationComplete) return;

    // Start first step if not started
    if (currentStep === 0) {
      const timer = setTimeout(() => {
        startStep(1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (!currentStepConfig) return;

    const messages = currentStepConfig.thinkingMessages;
    const messageInterval = currentStepConfig.duration / messages.length;

    // Progress through messages
    const messageTimer = setInterval(() => {
      if (currentMessageIndex < messages.length - 1) {
        setCurrentMessageIndex((prev) => prev + 1);
      } else {
        // All messages done, complete step and move to next
        clearInterval(messageTimer);
        setTimeout(() => {
          completeStep();
        }, 1600);
      }
    }, messageInterval);

    return () => clearInterval(messageTimer);
  }, [
    isAutoPlaying,
    currentStep,
    currentMessageIndex,
    currentStepConfig,
    startStep,
    completeStep,
    presentationComplete,
  ]);

  // Prevent auto-focus on subject input when email modal opens
  useEffect(() => {
    if (showEmailModal) {
      // Use requestAnimationFrame to ensure this runs after Dialog's focus logic
      const frameId = requestAnimationFrame(() => {
        const subjectInput = document.getElementById('email-subject');
        if (subjectInput && document.activeElement === subjectInput) {
          subjectInput.blur();
          // Also blur on the next frame to catch any delayed focus
          requestAnimationFrame(() => {
            if (document.activeElement === subjectInput) {
              subjectInput.blur();
            }
          });
        }
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [showEmailModal]);

  return (
    <div className="space-y-6">
      {/* Progress bar */}
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <StepProgressBar
          currentStep={currentStep}
          totalSteps={STEPS.length}
          stepStatuses={stepStatuses}
          stepTitles={STEPS.map((s) => s.title)}
        />
      </div>

      {/* Main presentation area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Active step pill and thinking animation */}
        <div className="space-y-4">
          {/* Initial state - waiting to start */}
          {currentStep === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-2xl border-2 border-dashed border-gray-300 p-8 text-center"
            >
              <div className="text-gray-500">
                <p className="text-lg font-medium mb-2">Ready to Process</p>
                <p className="text-sm">{fileName}</p>
              </div>
            </motion.div>
          )}

          {/* Active step */}
          {currentStepConfig && (
            <StepPill
              step={currentStep}
              title={currentStepConfig.title}
              description={currentStepConfig.description}
              status={stepStatuses[currentStep] || 'pending'}
              thinkingMessage={
                currentStepConfig.thinkingMessages[currentMessageIndex]
              }
              isActive={true}
            />
          )}

          {/* Thinking messages */}
          {currentStepConfig && stepStatuses[currentStep] === 'processing' && (
            <div className="mt-4">
              <ThinkingAnimation
                messages={currentStepConfig.thinkingMessages}
                currentMessageIndex={currentMessageIndex}
                isComplete={false}
                stepType={currentStepConfig.stepType}
              />
            </div>
          )}

          {/* Special displays */}
          <div className="flex flex-col items-center gap-4">
            <CurrencyRateDisplay
              fromCurrency="CAD"
              toCurrency="USD"
              rate={CAD_TO_USD_RATE}
              isVisible={showCurrencyRate}
            />
            <ValidationSummary
              totalRows={40}
              matchedRows={32}
              missingTransactions={8}
              isVisible={showValidationSummary}
            />
          </div>

          {/* AP Validation Decision Panel */}
          <AnimatePresence>
            {isApPaused && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-6"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-amber-900">
                      Missing Transactions Detected
                    </h3>
                    <p className="text-amber-700 mt-1">
                      8 transactions from this submission could not be matched
                      against our AP invoice records. How would you like to
                      proceed?
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={handleEmailLumina}
                    variant="outline"
                    size="lg"
                    className="h-auto py-4 border-amber-300 hover:bg-amber-100"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Mail className="h-6 w-6 text-amber-600" />
                      <span className="font-semibold">Email Lumina</span>
                      <span className="text-xs text-muted-foreground">
                        Request missing documentation
                      </span>
                    </div>
                  </Button>

                  <Button
                    onClick={handleProceed}
                    size="lg"
                    className="h-auto py-4 bg-green-600 hover:bg-green-700"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <ArrowRight className="h-6 w-6" />
                      <span className="font-semibold">Proceed Anyway</span>
                      <span className="text-xs opacity-80">
                        Continue with matched records
                      </span>
                    </div>
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Completion state with submit button */}
          {presentationComplete && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-2xl border-2 border-green-400 bg-green-50 p-6 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 300, delay: 0.2 }}
                className="mb-4"
              >
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-500 text-white">
                  <svg
                    className="h-8 w-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </motion.div>
              <h3 className="text-xl font-semibold text-green-900 mb-2">
                Processing Complete!
              </h3>
              <p className="text-green-700 mb-6">
                All transformations have been applied successfully.
              </p>
              {onSubmit && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button
                    onClick={onSubmit}
                    disabled={isSubmitting}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit to System'}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>

        {/* Right: Data table */}
        <div>
          <AnimatePresence mode="wait">
            {displayedDataStep > 0 && currentData.length > 0 && (
              <DataTransitionWrapper dataKey={`step-${displayedDataStep}`}>
                <AnimatedDataTable
                  data={currentData}
                  highlightedColumns={highlightedColumns}
                  highlightState={highlightState}
                  isTransitioning={isTransitioning}
                  maxRows={12}
                  title={
                    displayedDataStep === 1
                      ? 'Original Data (CAD)'
                      : displayedDataStep === 6
                        ? 'Final Transformed Data (USD)'
                        : `After Step ${displayedDataStep}: ${STEPS[displayedDataStep - 1]?.title}`
                  }
                />
              </DataTransitionWrapper>
            )}
          </AnimatePresence>

          {displayedDataStep === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center text-gray-400"
            >
              <p>Data preview will appear here</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Email Lumina Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent
          className="sm:max-w-[600px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Lumina
            </DialogTitle>
            <DialogDescription>
              Send a request for missing transaction documentation to Lumina.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-to">To</Label>
              <Input
                id="email-to"
                value="lumina-ap@supplier.com"
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-body">Message</Label>
              <MarkdownWYSIWYGEditor
                initialValue={emailBody}
                onChange={setEmailBody}
                placeholder="Write your message..."
                height={300}
                showToolbar={true}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
